/** `mine-platformer/bounty-bob` — the mine-platformer family template
 * authored from the stored Bounty Bob Strikes Back concept: a portrait,
 * single-screen mine shaft of catwalk tiers. Bob claims every catwalk
 * section his feet pass over; a fully claimed bay clears and he drops
 * through the hatch into the next, longer one (six bays win the shift).
 * Ladders (leave only at the top or bottom rung), one-way ore slides and
 * paired pneumatic transporters link the tiers; the jump is a fixed arc set
 * by take-off speed and cannot climb a tier. Falling two tiers dazes, more
 * than three kills. Mutant organisms patrol the upper tiers and telegraph
 * each turn for half a second; one touch costs a life (respawn at the bay
 * entrance, claims kept). A power item — one at a time, spread across
 * tiers, the next after the stun ends — stuns every mutant (harmless, not
 * destroyable) for six seconds. Bonus items float above the catwalks (+250,
 * +8 s). The shift clock (75 + 15·bay s, draining 15% faster after bay 1)
 * costs a life and resets when it runs out; time left converts to score.
 *
 * Built ONLY from maintained modules; constants follow the live game
 * (`bounty-bob-strikes-back`, seconds → integer ticks) where it matched the
 * concept. Where they differ the concept wins: a fall kills only past THREE
 * tiers (live: two). The six bays are generated here, at template time, by
 * a seeded bay generator that is fair by construction — so the spec stays
 * pure data a generator can read and patch. */
import type { GameSpecV1, ModuleUseV1 } from '../spec.js';
import type { DirectorConfig, PhaseDef, SpawnGroup } from '../modules/director.js';
import type { LayoutRect } from '../modules/surfaces.js';
import type { TemplateV1 } from './template.js';

const W = 9, H = 16, COLS = 12, CELL = W / COLS;
const TOP = 3.4, BOTTOM = 15.2, DECK = 0.28;
export const MINE_BAYS = 6;
const colX = (c: number) => (c + 0.5) * CELL;

/** mulberry32 — template-time only (the runtime has its own seeded rng). */
function rng(seed: number): { int(lo: number, hi: number): number; pick<T>(xs: readonly T[]): T } {
  let s = seed >>> 0;
  const next = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), s | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return { int: (lo, hi) => lo + Math.floor(next() * (hi - lo + 1)), pick: xs => xs[Math.floor(next() * xs.length)]! };
}

export interface MineBay { tiers: number[]; layout: LayoutRect[]; mutants: SpawnGroup[] }

/** One bay: `5 + index` tiers top→bottom (the bottom one is the entrance),
 * 1–2-cell gaps (two per odd tier from bay 3), a ladder up onto EVERY run
 * (never stacked on the one below), one ore slide, from bay 2 a transporter
 * pair whose top pad is the ONLY way onto the (unbroken) top tier,
 * `2 + index` mutants (never on the entrance tier, each on a run of ≥4
 * cells), three power-item slots on separate tiers and `3 + index` bonus
 * items above the decks. Fairness by construction: every run is reachable
 * from the entrance by ladders (and the transporter). */
export function mineBay(index: number, seed: number): MineBay {
  for (let attempt = 0; attempt < 64; attempt++) {
    const bay = tryBay(index, rng(seed * 7919 + index * 104729 + attempt * 15485863));
    if (bay) return bay;
  }
  throw new Error(`mine bay ${index}: no fair layout for seed ${seed}`);
}

function tryBay(index: number, r: ReturnType<typeof rng>): MineBay | null {
  const count = 5 + index;
  const gap = (BOTTOM - TOP) / (count - 1);
  const tiers = Array.from({ length: count }, (_, t) => TOP + t * gap);
  const teleports = index >= 1;
  const filled = tiers.map((_, t) => {
    const row = Array.from({ length: COLS }, () => true);
    if (t < count - 1 && !(teleports && t === 0)) {
      const holes = index >= 2 && t % 2 === 1 ? 2 : 1;
      for (let h = 0; h < holes; h++) {
        const width = r.int(1, 2), lo = h === 0 ? 2 : COLS / 2 + 1, hi = (h === 0 ? COLS / 2 - 2 : COLS - 3) - (width - 1);
        const start = r.int(lo, hi);
        for (let c = start; c < start + width; c++) row[c] = false;
      }
    }
    return row;
  });
  const special = tiers.map(() => Array.from({ length: COLS }, () => false));
  special[count - 1]![COLS / 2] = true; // the entrance hatch
  const layout: LayoutRect[] = [];
  // Catwalk runs (the claimable floor), as [first, last] columns per tier.
  const runs = tiers.map((y, t) => {
    const out: Array<[number, number]> = [];
    for (let c = 0; c < COLS;) {
      if (!filled[t]![c]) { c++; continue; }
      let end = c;
      while (end + 1 < COLS && filled[t]![end + 1]) end++;
      out.push([c, end]);
      layout.push({ archetype: 'catwalk', x: c * CELL, y, w: (end - c + 1) * CELL, h: DECK });
      c = end + 1;
    }
    return out;
  });
  // A ladder up onto every run from the tier below (the transporter's top tier excepted).
  for (let t = count - 2; t >= (teleports ? 1 : 0); t--) {
    for (const [c0, c1] of runs[t]!) {
      const cands = Array.from({ length: c1 - c0 + 1 }, (_, i) => c0 + i).filter(c => filled[t + 1]![c] && !special[t]![c] && !special[t + 1]![c]);
      if (!cands.length) return null;
      const c = r.pick(cands);
      layout.push({ archetype: 'mine-ladder', x: colX(c) - 0.25, y: tiers[t]!, w: 0.5, h: gap });
      special[t]![c] = true; special[t + 1]![c] = true;
    }
  }
  // One ore slide, three columns across and one tier down.
  for (let i = 0; i < 40; i++) {
    const t = r.int(teleports ? 1 : 0, count - 2), ct = r.int(1, COLS - 2), dir = r.pick([-1, 1] as const), cb = ct + dir * 3;
    if (cb < 0 || cb >= COLS || !filled[t]![ct] || special[t]![ct] || !filled[t + 1]![cb] || special[t + 1]![cb]) continue;
    layout.push({ archetype: 'ore-slide', x: colX(Math.min(ct, cb)), y: tiers[t]!, w: 3 * CELL, h: gap, dir });
    special[t]![ct] = true; special[t + 1]![cb] = true;
    break;
  }
  // Transporter pair: the top tier ↔ a tier at least two below.
  if (teleports) {
    let placed = false;
    for (let i = 0; i < 60 && !placed; i++) {
      const tb = r.int(2, count - 1), ca = r.int(1, COLS - 2), cb = r.int(1, COLS - 2);
      if (special[0]![ca] || !filled[tb]![cb] || special[tb]![cb] || Math.abs(ca - cb) < 2) continue;
      for (const [t, c] of [[tb, cb], [0, ca]] as const) {
        layout.push({ archetype: 'pneumatic-transporter', x: colX(c) - 0.3, y: tiers[t]! - 0.9, w: 0.6, h: 0.9 });
        special[t]![c] = true;
      }
      placed = true;
    }
    if (!placed) return null;
  }
  // Mutant patrols: upper tiers only, on runs of four or more cells (room to slip past).
  const mutants: SpawnGroup[] = [];
  for (let i = 0; i < 2 + index; i++) {
    // Round-robin over the upper tiers, skipping one without a long run.
    const t = Array.from({ length: count - 1 }, (_, k) => (i + k) % (count - 1)).find(k => runs[k]!.some(([c0, c1]) => c1 - c0 + 1 >= 4));
    if (t === undefined) return null;
    const [c0, c1] = r.pick(runs[t]!.filter(([a, b]) => b - a + 1 >= 4));
    mutants.push({ archetype: 'mutant-organism', count: 1, via: 'point', x: (c0 + c1 + 1) * CELL / 2, y: tiers[t]!, dir: r.pick([-1, 1]),
      delayTicks: 0, everyTicks: 0, jitterTicks: 30 });
  }
  // Power items on separate tiers; bonus items above free cells.
  const used = tiers.map(() => Array.from({ length: COLS }, () => false));
  const freeCols = (t: number) => Array.from({ length: COLS }, (_, c) => c).filter(c => filled[t]![c] && !special[t]![c] && !used[t]![c]);
  for (const t of [...new Set([0, Math.floor((count - 1) / 2), count - 2])]) {
    const cols = freeCols(t);
    if (!cols.length) return null;
    const c = r.pick(cols);
    used[t]![c] = true;
    layout.push({ archetype: 'power-item', x: colX(c) - 0.25, y: tiers[t]! - 0.7, w: 0.5, h: 0.7 });
  }
  for (let i = 0; i < 3 + index; i++) {
    const t = r.int(0, count - 1), cols = freeCols(t);
    if (!cols.length) continue;
    const c = r.pick(cols);
    used[t]![c] = true;
    layout.push({ archetype: 'bonus-item', x: colX(c) - 0.225, y: tiers[t]! - 0.6 - 0.45, w: 0.45, h: 0.45 });
  }
  // The entrance hatch on the bottom tier: the bay's respawn point.
  layout.push({ archetype: 'bay-hatch', x: colX(COLS / 2) - 0.45, y: BOTTOM - 0.3, w: 0.9, h: 0.3 });
  return { tiers, layout, mutants };
}


export const MINE_BAY_SEED = 1984;

export function mineBayPhases(seed = MINE_BAY_SEED, bays = MINE_BAYS): PhaseDef[] {
  return Array.from({ length: bays }, (_, i) => {
    const bay = mineBay(i, seed);
    return { kind: 'clear', spawns: bay.mutants, durationTicks: 0, clearBonus: 0, layout: bay.layout };
  });
}

const director: DirectorConfig = {
  phases: mineBayPhases(), loopFrom: -1, interPhaseTicks: 45, requiredTag: 'required', collectTag: 'power', sweepTag: 'phase-scoped',
};

const globals: ModuleUseV1[] = [
  { use: 'flow.director', config: director },
  { use: 'flow.escalation', config: { curves: [
    { scalar: 'shift.capacity', index: 'phase.number', kind: 'linear', base: 1, rate: 0.2, min: 1, max: 3 },
    { scalar: 'shift.drain', index: 'phase.number', kind: 'linear', base: 1, rate: 0.15, min: 1, max: 1.15 },
  ] } },
  { use: 'rule.resource', key: 'shift', config: { label: 'SHIFT', display: 'seconds', capacity: 2250, capacityScalar: 'shift.capacity',
    drainPerTick: 1, drainScalar: 'shift.drain', startRunning: false, fillOn: ['phase-started'],
    adjust: [{ signal: 'collected', fromTag: 'time-bonus', amount: 240 }], cashOutOn: ['phase-cleared'], cashOutPoints: 10 / 30,
    onEmpty: 'damage', victimTag: 'player' } },
  { use: 'rule.claimTiles', config: { claimerTag: 'player', surfaceTag: 'catwalk', cellW: CELL, score: 10, marker: 'claimed-catwalk', doneTag: 'required' } },
  { use: 'rule.broadcast', config: { on: 'collected', fromTag: 'power', signal: 'stun', toTag: 'enemy' } },
  { use: 'rule.takeTurns', config: { tag: 'power', firstDelayTicks: 60, gapTicks: 240, order: 'random', resetOn: ['phase-started'] } },
  { use: 'rule.contactDamage', config: { attackerTag: 'enemy', victimTag: 'player', exceptAttackerTags: ['vulnerable'], openingSafeTicks: 30, consumeAttacker: false } },
  { use: 'flow.objective', config: { win: 'phases-cleared', count: MINE_BAYS, milestone: 0, lose: 'players-out', requiredTag: 'required', collectTag: 'time-bonus', celebrate: true } },
];

const fixture = (role: string, tags: string[], size: { w: number; h: number }, palette: number, depth: number,
  extra: ModuleUseV1[] = []): GameSpecV1['archetypes'][string] => ({
  role, tags, size, render: { palette, depth, anchor: 'bottom' }, modules: [{ use: 'body.static', config: {} }, ...extra],
});

export const BOUNTY_BOB_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: 'Bounty Bob Strikes Back',
  summary: 'a single-screen mine-shaft platformer: claim every catwalk section of a bay by walking it',
  world: { w: W, h: H, orientation: 'portrait' },
  players: { archetypes: ['bounty-bob'], spawns: [{ x: colX(COLS / 2), y: BOTTOM, facing: 1 }] },
  archetypes: {
    'bounty-bob': {
      role: 'a stout miner in a hard hat with a headlamp, work overalls and empty gloved hands',
      tags: ['player'], size: { w: 0.55, h: 0.95 }, render: { palette: 1, depth: 30, anchor: 'bottom', shadow: true },
      modules: [
        // Instant run (accel/friction reach top speed in one tick) and a committed jump arc (no air control).
        { use: 'body.platformer', config: { run: 'momentum', maxSpeed: 5, accel: 400, airAccel: 0, friction: 400, iceFriction: 400,
          gravity: 20, jumpSpeed: 6.4, maxFall: 14, coyoteTicks: 2, jumpCut: 1, footSpan: 0.6, variableJump: false } },
        { use: 'traverse.ladder', config: { ladderTag: 'ladder', speed: 3.2, grabReach: 0.3 } },
        { use: 'traverse.slide', config: { slideTag: 'slide', speed: 6, reach: 0.35, enter: 'down', state: 'action-1' } },
        { use: 'traverse.teleport', config: { padTag: 'transporter', reach: 0.4, chargeTicks: 17, lockTicks: 18, enter: 'either', state: 'action-2' } },
        { use: 'core.vitals', config: { lives: 3, hurtTicks: 24, respawnTicks: 15, invulnTicks: 30 } },
        { use: 'rule.fallDamage', config: { measure: 'levels', dazeAt: 2, killAt: 4, dazeTicks: 36 } },
      ],
    },
    'mutant-organism': {
      role: 'a blobby radioactive crawler with glowing spots',
      tags: ['enemy', 'phase-scoped'], size: { w: 0.65, h: 0.6 }, render: { palette: 3, depth: 20, anchor: 'bottom' },
      modules: [
        { use: 'ai.walker', config: { speed: 1.8, gravity: 20, maxFall: 12, edge: 'turn', footSpan: 0.3, turnPauseTicks: 15, animate: true, airborneState: false } },
        { use: 'rule.vulnerableWindow', config: { hitsToFlip: 1, flipTicks: 180, requireGrounded: false, angrySpeedMul: 1, recoverSpeedMul: 1,
          flipScore: 0, defeatTicks: 1, hitSignals: ['stun'], dropSpeed: 0, flipPop: 0, recoverPop: 0, enrageState: false } },
      ],
    },
    catwalk: {
      role: 'a riveted steel grating catwalk section in dull grey-brown',
      tags: ['catwalk', 'required', 'scenery'], size: { w: CELL, h: DECK }, render: { palette: 9, depth: 2, anchor: 'center', surface: true },
      modules: [{ use: 'body.surface', config: { mode: 'oneWay', bumpable: false } }],
    },
    'claimed-catwalk': {
      role: 'a claimed catwalk section glowing cyan',
      tags: ['scenery'], size: { w: CELL, h: DECK }, render: { palette: 8, depth: 3, anchor: 'center', surface: true },
      modules: [{ use: 'body.static', config: {} }],
    },
    // Drawn like a surface: its (vertically tileable) art fills the instance's span.
    'mine-ladder': { ...fixture('a narrow rusted steel mine ladder', ['ladder', 'scenery'], { w: 0.5, h: 2 }, 5, 1),
      render: { palette: 5, depth: 1, anchor: 'bottom', surface: true } },
    // Drawn descending to the right; a left-running chute is its mirror (placed `dir`).
    'ore-slide': { ...fixture('an angled metal ore chute', ['slide', 'scenery'], { w: 2.25, h: 2 }, 6, 1),
      render: { palette: 6, depth: 1, anchor: 'bottom', facing: 'side' } },
    'pneumatic-transporter': fixture('a brass pneumatic transporter tube', ['transporter', 'scenery'],
      { w: 0.6, h: 0.9 }, 1, 4),
    'power-item': fixture('a glowing uranium canister', ['power', 'phase-scoped'], { w: 0.5, h: 0.7 }, 10, 10,
      [{ use: 'rule.pickup', config: { collectorTag: 'player', score: 500, collectOnHitFromBelow: false } }]),
    'bonus-item': fixture('a small glowing bonus lamp', ['time-bonus', 'phase-scoped'], { w: 0.45, h: 0.45 }, 7, 10,
      [{ use: 'rule.pickup', config: { collectorTag: 'player', score: 250, collectOnHitFromBelow: false } }]),
    'bay-hatch': fixture('a round steel bay entrance hatch', ['scenery'], { w: 0.9, h: 0.3 }, 2, 1,
      [{ use: 'rule.checkpoint', config: { playerTag: 'player', warp: true, touch: false } }]),
  },
  globals,
  hud: [{ from: 'score', field: 'score' }, { from: 'core.vitals', field: 'lives' }, { from: 'flow.director', field: 'phase', label: 'BAY' },
    { from: 'shift', field: 'level' }, { from: 'rule.claimTiles', field: 'claimed' }],
  input: { profile: 'move4-fire', labels: { a: 'JUMP' } },
  presentation: {
    projection: 'side', backgroundRole: 'a tall cutaway of an abandoned uranium mine: black rock walls, glinting green ore seams, amber lamp glow',
    palette: ['#000000', '#ffcc00', '#ff3b30', '#00e05a', '#2b6bff', '#a0522d', '#c0c8d0', '#ff00c8', '#00e5ff', '#6b6358', '#f4fff0'],
    backgroundPaletteIndex: 0,
  },
};

export const BOUNTY_BOB_TEMPLATE: TemplateV1 = {
  id: 'mine-platformer/bounty-bob',
  family: 'mine-platformer',
  title: 'Bounty Bob mine platformer',
  pitch: 'Portrait mine shaft: walk every catwalk section to claim the bay, climb ladders, ride slides and transporters, dodge mutants, beat the shift clock.',
  disclosure: {
    coreVerbs: ['walk', 'jump'],
    mechanics: [
      'A single-screen portrait mine bay of catwalk tiers; every catwalk section you walk over changes colour and counts as claimed.',
      'Claim every section of a bay to clear it: you drop through the entrance hatch into the next, taller bay. Six bays win the shift.',
      'Ladders link the tiers (step off only at the top or bottom rung), one-way ore slides carry you down a tier and paired pneumatic transporters fire you between tiers.',
      'The jump is a fixed arc that clears gaps but cannot climb a tier.',
      'Falling two tiers dazes you; falling more than three costs a life.',
      'Mutant organisms patrol the upper catwalks and pause before each turn; one touch costs a life, and you restart at the bay entrance with your claims kept.',
      'A pulsating power item stuns every mutant for a few seconds and banks bonus points; the next one appears elsewhere after the stun.',
      'Floating bonus items bank points and add shift time. The shift clock drains the whole time: running out costs a life and refills it; time left at a clear converts to score.',
    ],
    controls: 'Move left or right; up or down on a ladder climbs; down at a slide rides it; jump leaps a fixed arc.',
    winCondition: 'Claim every section of all six bays to win the shift; losing every life ends the game.',
  },
  music: { mood: 'tense jaunty underground mine shift', genre: 'retro arcade chiptune', tempoBpm: 132,
    instrumentation: ['square lead', 'pulse arpeggio', 'triangle bass', 'noise drums'] },
  spec: BOUNTY_BOB_SPEC,
  tunables: [
    { path: '/archetypes/bounty-bob/modules/body.platformer/maxSpeed', min: 4, max: 6 },
    { path: '/archetypes/bounty-bob/modules/traverse.ladder/speed', min: 2.4, max: 4.5 },
    { path: '/archetypes/bounty-bob/modules/core.vitals/lives', min: 1, max: 5, integer: true },
    { path: '/archetypes/bounty-bob/modules/rule.fallDamage/killAt', min: 3, max: 6, integer: true },
    { path: '/archetypes/mutant-organism/modules/ai.walker/speed', min: 1.2, max: 2.6 },
    { path: '/archetypes/mutant-organism/modules/ai.walker/turnPauseTicks', min: 9, max: 30, integer: true },
    { path: '/archetypes/mutant-organism/modules/rule.vulnerableWindow/flipTicks', min: 90, max: 300, integer: true },
    { path: '/globals/shift/capacity', min: 1500, max: 3600, integer: true },
    { path: '/globals/rule.takeTurns/gapTicks', min: 120, max: 600, integer: true },
    { path: '/globals/flow.director/interPhaseTicks', min: 30, max: 120, integer: true },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
  ],
  requirements: { events: ['claim', 'claim-complete', 'ladder-grab', 'ladder-exit', 'slide', 'teleport', 'collect', 'broadcast',
    'flip:mutant-organism', 'phase-clear', 'cash-out', 'checkpoint'] },
  signatureModules: ['rule.claimTiles', 'traverse.ladder', 'traverse.teleport', 'traverse.slide', 'rule.broadcast', 'flow.director'],
  // Bob starts on the unbroken entrance tier: walking moves him (claiming is
  // walking — the section he spawns on is claimed even idle, so it is no
  // separate verb); a jump is its own event. Climbing, sliding and
  // transporting need the bay's layout, which the solver proof covers
  // (required `claim`, `ladder-grab`, `slide` and `teleport` events).
  verbProofs: [
    { verb: 'walk', input: 'right', ms: 500, expect: { kind: 'state-change', path: 'seats.0.x' } },
    { verb: 'jump', input: 'a', ms: 300, expect: { kind: 'event', event: 'jump' } },
  ],
};
